import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import JsonLd from '@/components/base/JsonLd';
import { useSeo, SITE_URL, SITE_LOGO } from '@/hooks/useSeo';

const MAIN_URL = 'https://kaspreadsheet.com';

const ARTICLE_IMAGE =
  'https://readdy.ai/api/search-image?query=Organized%20product%20spreadsheet%20on%20laptop%20screen%20surrounded%20by%20fashion%20items%20sneakers%20bags%20accessories%20on%20a%20warm%20terracotta%20workspace%20editorial%20overhead%20photography%20neutral%20tones&width=1600&height=600&seq=achb-article-hero-v3&orientation=landscape';

const TOC_ITEMS = [
  { id: 'what-is', label: 'What Is an AllChinaBuy Spreadsheet?' },
  { id: 'why-use', label: 'Why Use the AllChinaBuy Spreadsheet?' },
  { id: 'how-to-use', label: 'How to Use the Spreadsheet Step by Step' },
  { id: 'categories', label: 'Spreadsheet Categories Explained' },
  { id: 'qc-guide', label: 'Quality Control (QC) Guide' },
  { id: 'shipping', label: 'Shipping Costs & Lines' },
  { id: 'comparison', label: 'AllChinaBuy vs Other Agents' },
  { id: 'tips', label: 'Pro Tips for Smarter Shopping' },
  { id: 'faq', label: 'Frequently Asked Questions' },
];

export default function Article() {
  const { t, i18n } = useTranslation();

  useSeo({
    title: t('article.metaTitle'),
    description: t('article.metaDescription'),
    keywords: 'AllChinaBuy spreadsheet, AllChinaBuy guide, AllChinaBuy QC, AllChinaBuy shipping',
    path: '/allchinabuy-spreadsheet-guide',
    image: ARTICLE_IMAGE,
    lang: i18n.language,
  });

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: t('article.metaTitle'),
    description: t('article.metaDescription'),
    image: [ARTICLE_IMAGE],
    inLanguage: i18n.language?.split('-')[0] ?? 'en',
    datePublished: '2026-01-01',
    dateModified: '2026-08-24',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${SITE_URL}/allchinabuy-spreadsheet-guide`,
    },
    author: { '@type': 'Organization', name: 'AllChinaBuy Spreadsheet' },
    publisher: {
      '@type': 'Organization',
      name: 'AllChinaBuy Spreadsheet',
      logo: { '@type': 'ImageObject', url: SITE_LOGO },
    },
  };

  return (
    <div className="min-h-screen bg-background-50">
      <JsonLd data={jsonLd} />
      <Navbar />

      <main className="pt-20">
        {/* Hero banner */}
        <div className="relative overflow-hidden bg-foreground-900 py-20 md:py-28">
          <div className="absolute inset-0 opacity-20">
            <img
              src="https://readdy.ai/api/search-image?query=Organized%20product%20spreadsheet%20on%20laptop%20screen%20surrounded%20by%20fashion%20items%20sneakers%20bags%20accessories%20on%20a%20warm%20terracotta%20workspace%20editorial%20overhead%20photography%20neutral%20tones&width=1600&height=600&seq=achb-article-hero-v3&orientation=landscape"
              alt=""
              className="w-full h-full object-cover"
            />
          </div>
          <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
            <nav aria-label="Breadcrumb" className="mb-6">
              <ol className="flex flex-wrap items-center justify-center gap-1.5 text-sm text-white/50">
                <li>
                  <Link to="/" className="hover:text-white/80 transition-colors">
                    Home
                  </Link>
                </li>
                <li aria-hidden="true">/</li>
                <li className="text-white/30">Spreadsheet Guide</li>
              </ol>
            </nav>
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 border border-white/20 mb-6">
              <i className="ri-book-open-line text-accent-400 text-sm"></i>
              <span className="text-white/80 text-xs font-medium tracking-wider uppercase">
                Complete Guide · 2026 Edition
              </span>
            </div>
            <h1 className="font-heading text-3xl md:text-5xl font-bold text-white leading-tight mb-6">
              AllChinaBuy Spreadsheet 2026{' '}
              <span className="text-primary-300">— The Complete Guide</span>
            </h1>
            <p className="text-white/60 text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
              Everything you need to know about finding, using, and maximizing AllChinaBuy
              spreadsheets — from beginner basics to advanced QC and shipping strategies.
            </p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-6 text-white/40 text-sm">
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line"></i> 18 min read
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line"></i> Updated August 2026
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-link text-accent-400"></i>
                <Link to="/" className="hover:text-white/70 transition-colors">
                  allchinabuyspread.wtf
                </Link>
              </span>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-6 md:px-10 py-16">
          <div className="flex flex-col lg:flex-row gap-12">
            {/* Sticky TOC sidebar */}
            <aside className="hidden lg:block lg:w-72 shrink-0">
              <div className="sticky top-24">
                <div className="bg-white border border-background-200/70 rounded-2xl p-6">
                  <h2 className="font-heading text-sm font-semibold text-foreground-900 uppercase tracking-wider mb-4">
                    Table of Contents
                  </h2>
                  <nav>
                    <ol className="space-y-2">
                      {TOC_ITEMS.map((item, i) => (
                        <li key={item.id}>
                          <a
                            href={`#${item.id}`}
                            className="flex items-start gap-2.5 text-sm text-foreground-600 hover:text-primary-600 transition-colors duration-200 group"
                          >
                            <span className="flex-shrink-0 w-5 h-5 rounded-full bg-background-100 group-hover:bg-primary-100 flex items-center justify-center text-xs font-medium text-foreground-500 group-hover:text-primary-600 transition-colors">
                              {i + 1}
                            </span>
                            <span className="leading-snug">{item.label}</span>
                          </a>
                        </li>
                      ))}
                    </ol>
                  </nav>
                  <div className="mt-6 pt-5 border-t border-background-200">
                    <a
                      href={MAIN_URL}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center justify-center gap-2 w-full px-4 py-2.5 rounded-xl bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      Browse Spreadsheet
                      <i className="ri-arrow-right-up-line"></i>
                    </a>
                  </div>
                </div>

                <div className="mt-6 bg-accent-50 border border-accent-200/50 rounded-2xl p-6">
                  <p className="text-xs text-accent-700 font-medium mb-1">
                    Independent Resource
                  </p>
                  <p className="text-xs text-foreground-500 leading-relaxed">
                    This site is not affiliated with AllChinaBuy. We are an independent guide for
                    smarter shopping decisions.
                  </p>
                </div>
              </div>
            </aside>

            {/* Article body */}
            <article className="flex-1 min-w-0">
              {/* What Is section */}
              <section id="what-is" className="mb-14">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-6 flex items-center gap-3">
                  <span className="flex-shrink-0 w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center text-sm font-bold text-primary-600">
                    1
                  </span>
                  What Is an AllChinaBuy Spreadsheet?
                </h2>
                <p className="text-foreground-700 text-base leading-relaxed mb-4">
                  An{' '}
                  <strong className="text-foreground-900 font-semibold">
                    AllChinaBuy spreadsheet
                  </strong>{' '}
                  is a curated, shareable list of product links organized in a structured format —
                  typically Google Sheets or a similar tool — that allows shoppers to browse
                  thousands of verified product finds in one place. Instead of endlessly scrolling
                  through Taobao, Weidian, or 1688, you open a single organized table and jump
                  straight to the exact items that match your style, budget, and quality
                  requirements.
                </p>
                <p className="text-foreground-700 text-base leading-relaxed mb-4">
                  Originally developed by the streetwear and rep community, the{' '}
                  <strong className="text-foreground-900">AllChinaBuy spreadsheet</strong> has
                  evolved from a niche tool into an essential resource for thousands of
                  international buyers in 2026. It typically includes product names, direct
                  shopping links, price ranges, seller ratings, QC photo references, size guides,
                  and availability notes.
                </p>
                <div className="bg-background-100 border-l-4 border-primary-400 rounded-r-xl p-5 my-6">
                  <p className="text-foreground-700 text-sm leading-relaxed">
                    <strong className="text-foreground-900">Quick definition:</strong> A spreadsheet
                    is a community-maintained, organized catalog of product links from Chinese
                    marketplaces. It saves you research time, reduces the risk of bad purchases, and
                    helps you discover items you would never have found on your own.
                  </p>
                </div>
                <p className="text-foreground-700 text-base leading-relaxed">
                  The best spreadsheets are regularly updated — usually weekly — to reflect new
                  product drops, seller changes, pricing fluctuations, and community reviews. Our
                  directory on this site connects you directly to the most current and comprehensive{' '}
                  <strong className="text-foreground-900">AllChinaBuy spreadsheet 2026</strong>{' '}
                  collections available.
                </p>
              </section>

              {/* Why Use section */}
              <section id="why-use" className="mb-14">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-6 flex items-center gap-3">
                  <span className="flex-shrink-0 w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center text-sm font-bold text-primary-600">
                    2
                  </span>
                  Why Use the AllChinaBuy Spreadsheet?
                </h2>
                <p className="text-foreground-700 text-base leading-relaxed mb-6">
                  Without a spreadsheet, finding high-quality products on Chinese shopping platforms
                  can be overwhelming. The sheer volume of listings, language barriers, varying
                  seller reliability, and confusing categories make it easy to waste time or make
                  poor purchases. An{' '}
                  <strong className="text-foreground-900">AllChinaBuy spreadsheet</strong> solves
                  all of these problems in one go.
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                  {[
                    {
                      icon: 'ri-time-line',
                      title: 'Save Hours of Research',
                      desc: 'Skip the endless manual search. A well-curated spreadsheet does the hard work for you, surfacing only the best picks in each category.',
                    },
                    {
                      icon: 'ri-shield-check-line',
                      title: 'Verified Seller Quality',
                      desc: 'Community-reviewed entries include seller ratings and past buyer feedback, dramatically reducing the risk of receiving low-quality or wrong items.',
                    },
                    {
                      icon: 'ri-camera-line',
                      title: 'Real QC Photos',
                      desc: 'Many spreadsheet entries link to actual QC photos from previous buyers, letting you assess quality before committing to an order.',
                    },
                    {
                      icon: 'ri-price-tag-3-line',
                      title: 'Better Price Awareness',
                      desc: 'Comparing multiple spreadsheet entries helps you understand fair price ranges and avoid overpaying for items widely available at lower costs.',
                    },
                    {
                      icon: 'ri-layout-grid-line',
                      title: 'Organized by Category',
                      desc: 'Shoes, hoodies, jerseys, bags, accessories — everything is sorted so you spend time buying, not searching.',
                    },
                    {
                      icon: 'ri-community-line',
                      title: 'Community Knowledge',
                      desc: 'Spreadsheets carry the collective experience of thousands of buyers. Tips, warnings, and recommendations built in directly.',
                    },
                  ].map((item) => (
                    <div
                      key={item.title}
                      className="bg-white border border-background-200/70 rounded-xl p-5"
                    >
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-8 h-8 rounded-lg bg-accent-100 flex items-center justify-center">
                          <i className={`${item.icon} text-accent-600 text-base`}></i>
                        </div>
                        <h3 className="font-semibold text-foreground-900 text-sm">{item.title}</h3>
                      </div>
                      <p className="text-foreground-600 text-sm leading-relaxed">{item.desc}</p>
                    </div>
                  ))}
                </div>
              </section>

              {/* How to Use section */}
              <section id="how-to-use" className="mb-14">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-6 flex items-center gap-3">
                  <span className="flex-shrink-0 w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center text-sm font-bold text-primary-600">
                    3
                  </span>
                  How to Use the Spreadsheet Step by Step
                </h2>
                <p className="text-foreground-700 text-base leading-relaxed mb-6">
                  Using the{' '}
                  <strong className="text-foreground-900">AllChinaBuy spreadsheet</strong> is
                  straightforward once you understand the workflow. Here is the complete process from
                  discovery to delivery.
                </p>

                <div className="space-y-4">
                  {[
                    {
                      step: '01',
                      title: 'Browse the Category Directory',
                      desc: 'Start on this page and choose a category from the dropdown or category grid. Each category links directly to the corresponding spreadsheet section on our main store.',
                    },
                    {
                      step: '02',
                      title: 'Find a Product Link',
                      desc: 'Inside the spreadsheet, scan the rows for products that match your criteria. Look at the seller name, price range, QC photo links, and any community notes before deciding.',
                    },
                    {
                      step: '03',
                      title: 'Copy the Product Link',
                      desc: "Copy the direct product URL from the spreadsheet. This is usually a Taobao, Weidian, or 1688 link that points to the exact seller's listing.",
                    },
                    {
                      step: '04',
                      title: 'Paste Into AllChinaBuy Agent',
                      desc: 'Log into your AllChinaBuy account, paste the product link into the search bar, and the agent will pull up the listing and allow you to configure options like size, color, and quantity.',
                    },
                    {
                      step: '05',
                      title: 'Review QC Photos at Warehouse',
                      desc: 'After the agent purchases your item, it will be stored at their warehouse. Review the QC photos they send before approving shipment to ensure the item matches expectations.',
                    },
                    {
                      step: '06',
                      title: 'Choose a Shipping Line and Ship',
                      desc: 'Select a shipping line based on your budget and timeline. Consolidating multiple items into one shipment saves significantly on per-item shipping costs.',
                    },
                  ].map((item) => (
                    <div key={item.step} className="flex gap-5">
                      <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-primary-500 flex items-center justify-center text-white font-bold text-sm">
                        {item.step}
                      </div>
                      <div className="flex-1 pt-1">
                        <h3 className="font-semibold text-foreground-900 mb-1">{item.title}</h3>
                        <p className="text-foreground-600 text-sm leading-relaxed">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              {/* Categories section */}
              <section id="categories" className="mb-14">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-6 flex items-center gap-3">
                  <span className="flex-shrink-0 w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center text-sm font-bold text-primary-600">
                    4
                  </span>
                  Spreadsheet Categories Explained
                </h2>
                <p className="text-foreground-700 text-base leading-relaxed mb-6">
                  The{' '}
                  <strong className="text-foreground-900">AllChinaBuy spreadsheet</strong> covers 14
                  main product categories. Each category has its own curated list updated regularly
                  with the latest finds, verified seller links, and community QC references.
                </p>

                <div className="overflow-x-auto">
                  <table className="w-full text-sm border-collapse">
                    <thead>
                      <tr className="bg-background-100">
                        <th className="text-left px-4 py-3 font-semibold text-foreground-800 border border-background-200">
                          Category
                        </th>
                        <th className="text-left px-4 py-3 font-semibold text-foreground-800 border border-background-200">
                          What You'll Find
                        </th>
                        <th className="text-left px-4 py-3 font-semibold text-foreground-800 border border-background-200">
                          Popularity
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        ['Shoes', 'Sneakers, dress shoes, boots, sandals', '★★★★★'],
                        ['Hoodies/Sweaters', 'Oversized hoodies, zip-ups, crewnecks', '★★★★★'],
                        ['T-Shirts', 'Graphic tees, plain tees, polo shirts', '★★★★☆'],
                        ['Jackets', 'Bombers, windbreakers, puffer jackets', '★★★★☆'],
                        ['Pants/Shorts', 'Cargo pants, track pants, shorts', '★★★★☆'],
                        ['Headwear', 'Caps, beanies, bucket hats', '★★★☆☆'],
                        ['Bags', 'Backpacks, crossbodies, totes, duffle bags', '★★★★☆'],
                        ['Accessories', 'Watches, sunglasses, belts, jewelry', '★★★☆☆'],
                        ['Jersey', 'Football, basketball, soccer jerseys', '★★★★☆'],
                        ['Perfumes', 'Designer fragrances, body mists', '★★★☆☆'],
                      ].map(([cat, desc, pop]) => (
                        <tr key={cat} className="hover:bg-background-50 transition-colors">
                          <td className="px-4 py-3 font-medium text-foreground-900 border border-background-200">
                            {cat}
                          </td>
                          <td className="px-4 py-3 text-foreground-600 border border-background-200">
                            {desc}
                          </td>
                          <td className="px-4 py-3 text-foreground-600 border border-background-200">
                            {pop}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <p className="text-foreground-600 text-sm mt-4">
                  Browse all categories on the{' '}
                  <Link to="/#categories" className="text-primary-600 hover:underline font-medium">
                    AllChinaBuy Spreadsheet homepage
                  </Link>
                  .
                </p>
              </section>

              {/* QC Guide section */}
              <section id="qc-guide" className="mb-14">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-6 flex items-center gap-3">
                  <span className="flex-shrink-0 w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center text-sm font-bold text-primary-600">
                    5
                  </span>
                  Quality Control (QC) Guide
                </h2>
                <p className="text-foreground-700 text-base leading-relaxed mb-4">
                  QC — quality control — is one of the most important steps in the AllChinaBuy
                  spreadsheet workflow. When your item arrives at the warehouse, the agent photographs
                  it so you can inspect it before approving shipment. Getting this step right prevents
                  receiving items that do not match what you expected.
                </p>

                <div className="bg-accent-50 border border-accent-200/50 rounded-xl p-6 mb-6">
                  <h3 className="font-semibold text-foreground-900 mb-3 flex items-center gap-2">
                    <i className="ri-eye-line text-accent-600"></i>
                    What to Check in QC Photos
                  </h3>
                  <ul className="space-y-2">
                    {[
                      'Color accuracy — compare with the original listing and reference photos',
                      'Logo placement — check alignment, font weight, and sizing',
                      'Stitching quality — zoom into seams, hems, and embroidery',
                      'Material texture — does the fabric look like the correct weight?',
                      'Tag and label placement — should match reference images closely',
                      'Size verification — confirm sizing sticker or tag matches your order',
                    ].map((item) => (
                      <li key={item} className="flex items-start gap-2 text-sm text-foreground-700">
                        <i className="ri-check-line text-accent-600 mt-0.5 flex-shrink-0"></i>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>

                <p className="text-foreground-700 text-base leading-relaxed mb-4">
                  If the QC photos reveal any issues — wrong color, poor stitching, incorrect logo —
                  you can request a return or exchange before the item ever leaves the warehouse. This
                  is one of the key advantages of using a spreadsheet-based workflow rather than
                  ordering directly.
                </p>
                <p className="text-foreground-600 text-sm">
                  For a deeper dive, read our dedicated{' '}
                  <Link
                    to="/allchinabuy-qc"
                    className="text-primary-600 hover:underline font-medium"
                  >
                    AllChinaBuy QC Guide
                  </Link>
                  .
                </p>
              </section>

              {/* Shipping section */}
              <section id="shipping" className="mb-14">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-6 flex items-center gap-3">
                  <span className="flex-shrink-0 w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center text-sm font-bold text-primary-600">
                    6
                  </span>
                  Shipping Costs &amp; Lines
                </h2>
                <p className="text-foreground-700 text-base leading-relaxed mb-6">
                  Shipping is often the biggest variable cost when shopping with the{' '}
                  <strong className="text-foreground-900">AllChinaBuy spreadsheet</strong>. Your
                  total shipping cost depends on the weight and dimensions of your parcel, your chosen
                  shipping line, and whether you consolidate multiple items.
                </p>

                <div className="overflow-x-auto mb-6">
                  <table className="w-full text-sm border-collapse">
                    <thead>
                      <tr className="bg-primary-50">
                        <th className="text-left px-4 py-3 font-semibold text-foreground-800 border border-background-200">
                          Shipping Type
                        </th>
                        <th className="text-left px-4 py-3 font-semibold text-foreground-800 border border-background-200">
                          Estimated Time
                        </th>
                        <th className="text-left px-4 py-3 font-semibold text-foreground-800 border border-background-200">
                          Cost Level
                        </th>
                        <th className="text-left px-4 py-3 font-semibold text-foreground-800 border border-background-200">
                          Best For
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        ['Economy Sea', '4–8 weeks', 'Very Low', 'Heavy, non-urgent parcels'],
                        ['Economy Air', '2–4 weeks', 'Low', 'Most everyday orders'],
                        ['Standard Air', '10–18 days', 'Medium', 'Balance of speed and cost'],
                        ['Express (DHL/UPS)', '5–10 days', 'High', 'Time-sensitive items'],
                      ].map(([type, time, cost, best]) => (
                        <tr key={type} className="hover:bg-background-50 transition-colors">
                          <td className="px-4 py-3 font-medium text-foreground-900 border border-background-200">
                            {type}
                          </td>
                          <td className="px-4 py-3 text-foreground-600 border border-background-200">
                            {time}
                          </td>
                          <td className="px-4 py-3 text-foreground-600 border border-background-200">
                            {cost}
                          </td>
                          <td className="px-4 py-3 text-foreground-600 border border-background-200">
                            {best}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="bg-background-100 rounded-xl p-5">
                  <h3 className="font-semibold text-foreground-900 mb-3">
                    Top Shipping Cost-Saving Tips
                  </h3>
                  <ul className="space-y-2">
                    {[
                      'Consolidate multiple spreadsheet orders into one shipment to share base costs',
                      'Remove shoe boxes, tags, and excess packaging to reduce volumetric weight',
                      'Plan hauls around seasonal sale periods to stretch your budget further',
                      'Use the shipping calculator on your agent dashboard before confirming',
                    ].map((tip) => (
                      <li key={tip} className="flex items-start gap-2 text-sm text-foreground-700">
                        <i className="ri-lightbulb-line text-primary-500 mt-0.5 flex-shrink-0"></i>
                        {tip}
                      </li>
                    ))}
                  </ul>
                </div>

                <p className="text-foreground-600 text-sm mt-4">
                  Read the full breakdown in our{' '}
                  <Link
                    to="/allchinabuy-shipping-cost"
                    className="text-primary-600 hover:underline font-medium"
                  >
                    AllChinaBuy Shipping Cost Guide
                  </Link>
                  .
                </p>
              </section>

              {/* Comparison section */}
              <section id="comparison" className="mb-14">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-6 flex items-center gap-3">
                  <span className="flex-shrink-0 w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center text-sm font-bold text-primary-600">
                    7
                  </span>
                  AllChinaBuy vs Other Agents
                </h2>
                <p className="text-foreground-700 text-base leading-relaxed mb-6">
                  AllChinaBuy is not the only shopping agent that supports spreadsheet-based buying,
                  but it has built one of the strongest reputations among the community. Here is how
                  it compares to the main alternatives.
                </p>

                <div className="overflow-x-auto">
                  <table className="w-full text-sm border-collapse">
                    <thead>
                      <tr className="bg-background-100">
                        <th className="text-left px-4 py-3 font-semibold text-foreground-800 border border-background-200">
                          Agent
                        </th>
                        <th className="text-left px-4 py-3 font-semibold text-foreground-800 border border-background-200">
                          Fee Structure
                        </th>
                        <th className="text-left px-4 py-3 font-semibold text-foreground-800 border border-background-200">
                          Spreadsheet Community
                        </th>
                        <th className="text-left px-4 py-3 font-semibold text-foreground-800 border border-background-200">
                          QC Quality
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        ['AllChinaBuy', 'Competitive', 'Very Strong', 'Excellent'],
                        ['Superbuy', 'Premium', 'Strong', 'Excellent'],
                        ['CSSBuy', 'Low', 'Moderate', 'Good'],
                        ['Sugargoo', 'Low', 'Growing', 'Good'],
                        ['Basetao', 'Low', 'Moderate', 'Good'],
                      ].map(([agent, fee, community, qc]) => (
                        <tr key={agent} className="hover:bg-background-50 transition-colors">
                          <td className="px-4 py-3 font-medium text-foreground-900 border border-background-200">
                            {agent}
                          </td>
                          <td className="px-4 py-3 text-foreground-600 border border-background-200">
                            {fee}
                          </td>
                          <td className="px-4 py-3 text-foreground-600 border border-background-200">
                            {community}
                          </td>
                          <td className="px-4 py-3 text-foreground-600 border border-background-200">
                            {qc}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <p className="text-foreground-600 text-sm mt-4">
                  See the full breakdown in{' '}
                  <Link
                    to="/allchinabuy-alternatives"
                    className="text-primary-600 hover:underline font-medium"
                  >
                    AllChinaBuy Alternatives: How Other Agents Compare
                  </Link>
                  .
                </p>
              </section>

              {/* Pro Tips section */}
              <section id="tips" className="mb-14">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-6 flex items-center gap-3">
                  <span className="flex-shrink-0 w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center text-sm font-bold text-primary-600">
                    8
                  </span>
                  Pro Tips for Smarter Shopping
                </h2>
                <p className="text-foreground-700 text-base leading-relaxed mb-6">
                  Once you are comfortable with the basics, these advanced strategies from
                  experienced spreadsheet users will help you get more value from every order.
                </p>

                <div className="space-y-4">
                  {[
                    {
                      num: '01',
                      title: 'Always Cross-Reference QC With Community Photos',
                      body: "Before approving QC, search community forums and subreddits for other buyers' photos of the same item from the same seller. A pattern of complaints is a red flag even if your QC photos look acceptable.",
                    },
                    {
                      num: '02',
                      title: 'Test Sellers With Small Orders First',
                      body: 'If you are trying a new seller from the spreadsheet for the first time, place a small single-item order. If the seller quality, QC, and shipping experience are good, scale up on the next haul.',
                    },
                    {
                      num: '03',
                      title: 'Bookmark the Best Spreadsheet Finds',
                      body: "Popular items sell out quickly. When you find a great spreadsheet entry, add it to your agent cart or a personal saved list so you don't lose track of it.",
                    },
                    {
                      num: '04',
                      title: 'Plan Hauls Around Low Shipping Seasons',
                      body: 'Shipping costs spike during peak periods like Chinese New Year and December. Planning hauls for mid-year or early autumn often results in faster processing and lower shipping rates.',
                    },
                    {
                      num: '05',
                      title: 'Use the Discount Code Section Strategically',
                      body: 'Combine agent platform coupons with consolidation benefits for maximum savings. Read our dedicated coupon guide to find current valid codes before you check out.',
                    },
                  ].map((tip) => (
                    <div
                      key={tip.num}
                      className="bg-white border border-background-200/70 rounded-xl p-5 flex gap-4"
                    >
                      <span className="flex-shrink-0 font-heading text-3xl font-bold text-primary-200 leading-none">
                        {tip.num}
                      </span>
                      <div>
                        <h3 className="font-semibold text-foreground-900 mb-2">{tip.title}</h3>
                        <p className="text-foreground-600 text-sm leading-relaxed">{tip.body}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              {/* FAQ section */}
              <section id="faq" className="mb-14">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-6 flex items-center gap-3">
                  <span className="flex-shrink-0 w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center text-sm font-bold text-primary-600">
                    9
                  </span>
                  Frequently Asked Questions
                </h2>

                <div className="space-y-4">
                  {[
                    {
                      q: 'Is AllChinaBuy a legitimate service?',
                      a: "Yes. AllChinaBuy is a legitimate shopping agent used by thousands of international buyers. As with any cross-border service, verify sellers through QC photos and use secure payment methods. Read our full safety guide for more detail.",
                    },
                    {
                      q: 'Do I need an account to use the spreadsheet?',
                      a: 'No. You can browse this directory and the spreadsheet freely without an account. You only need an AllChinaBuy account when you are ready to place an order through their agent platform.',
                    },
                    {
                      q: 'How often is the AllChinaBuy spreadsheet 2026 updated?',
                      a: 'Our directory is updated weekly. Individual spreadsheets on the main store are maintained by the community and updated whenever new finds are verified or old listings become unavailable.',
                    },
                    {
                      q: 'Can I get a refund if the item is wrong?',
                      a: 'Most agents offer a return or exchange process if items fail QC inspection at the warehouse. Policies vary by seller and agent. Always review QC photos before approving shipment to avoid issues after delivery.',
                    },
                    {
                      q: 'What is the best category on the AllChinaBuy spreadsheet?',
                      a: 'Shoes and hoodies are consistently the most popular categories with the highest volume of verified finds and community-reviewed sellers. Bags and accessories are also highly active.',
                    },
                    {
                      q: 'Is this an official AllChinaBuy website?',
                      a: 'No. This is an independent information and directory site. We are not affiliated with, endorsed by, or sponsored by AllChinaBuy. Always visit the official AllChinaBuy platform for transactions.',
                    },
                  ].map((item) => (
                    <details
                      key={item.q}
                      className="group bg-white border border-background-200/70 rounded-xl overflow-hidden"
                    >
                      <summary className="flex items-center justify-between px-5 py-4 cursor-pointer select-none">
                        <span className="font-semibold text-foreground-900 text-sm md:text-base pr-4">
                          {item.q}
                        </span>
                        <div className="w-6 h-6 flex-shrink-0 flex items-center justify-center rounded-full bg-background-100 group-open:bg-primary-100 transition-colors">
                          <i className="ri-add-line text-foreground-600 text-sm group-open:hidden"></i>
                          <i className="ri-subtract-line text-primary-600 text-sm hidden group-open:block"></i>
                        </div>
                      </summary>
                      <div className="px-5 pb-4 text-foreground-600 text-sm leading-relaxed">
                        <p>{item.a}</p>
                      </div>
                    </details>
                  ))}
                </div>
              </section>

              {/* Internal links + CTA */}
              <section className="bg-white border border-background-200/70 rounded-2xl p-6 md:p-8 mb-10">
                <h2 className="font-heading text-lg font-semibold text-foreground-900 mb-4">
                  Related Guides
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
                  {[
                    { to: '/allchinabuy-review', label: 'AllChinaBuy Review 2026' },
                    { to: '/allchinabuy-shipping-cost', label: 'AllChinaBuy Shipping Cost Guide' },
                    { to: '/allchinabuy-qc', label: 'AllChinaBuy QC Guide' },
                    { to: '/allchinabuy-warehouse', label: 'AllChinaBuy Warehouse Guide' },
                    { to: '/allchinabuy-legit', label: 'Is AllChinaBuy Legit?' },
                    { to: '/allchinabuy-coupon', label: 'AllChinaBuy Coupon Guide' },
                    { to: '/allchinabuy-tracking', label: 'AllChinaBuy Tracking Guide' },
                    { to: '/allchinabuy-alternatives', label: 'AllChinaBuy Alternatives' },
                  ].map((link) => (
                    <Link
                      key={link.to}
                      to={link.to}
                      className="flex items-center gap-2 text-sm text-foreground-700 hover:text-primary-600 transition-colors group"
                    >
                      <i className="ri-article-line text-foreground-400 group-hover:text-primary-400 transition-colors"></i>
                      {link.label}
                    </Link>
                  ))}
                </div>
                <div className="flex flex-col sm:flex-row gap-3 pt-5 border-t border-background-200">
                  <a
                    href={MAIN_URL}
                    target="_blank"
                    rel="noopener nofollow"
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-primary-500 text-white font-semibold text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Browse AllChinaBuy Spreadsheet
                    <i className="ri-arrow-right-up-line"></i>
                  </a>
                  <Link
                    to="/"
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl border border-background-300 text-foreground-800 font-medium text-sm hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-home-4-line"></i>
                    Back to Home
                  </Link>
                </div>
              </section>

              <div className="text-xs text-foreground-400 border-t border-background-200 pt-6">
                <p>
                  This article is an independent guide and is not affiliated with AllChinaBuy. We may earn a commission from qualifying purchases through links on this site.
                </p>
              </div>
            </article>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}